// account-avatar.js — avatar upload + delete + preview for the
// Account page. Wires the file-picker / clear-button click
// handlers at module init.
//
// Public exports:
//   refreshAccountAvatarPreview(user)
//     Paint the Account page's avatar preview slot. Tries the
//     uploaded avatar (cached on whoami), then Gravatar (if the
//     user has an email), then an initial-letter SVG.
//     pages/account.js calls this (via the AC_LEGACY bridge)
//     from its render path.
//
// Module-internal: uploadAvatarDataURL, deleteAvatar, plus the
// wireAvatarUpload() side-effect at module init.
//
// Hoisted from bootstrap.js on 2026-05-13 (Phase K Tier 2 prep).
import { $ } from "./dom.js";
import { showToast } from "./ui.js";
import {
  gravatarURL,
  inlineInitialAvatar,
  resizeImageToDataURL,
} from "./avatar.js";
import { getWhoami, patchWhoami } from "./whoami.js";

// COORDINATOR is the cloud API base; same access path bootstrap.js
// uses. Empty string in daemon mode (same-origin).
const COORDINATOR =
  (typeof window !== "undefined" &&
    window.AC_CONFIG &&
    window.AC_CONFIG.API_BASE) ||
  "";

// supabase + loadAndRenderWhoami live in bootstrap.js's closure.
// We read them via the AC_LEGACY bridge at call time. By the time
// any of these handlers fire (file picker change, clear-button
// click), bootstrap.js's init IIFE has long since populated
// window.AC_LEGACY.
function getSupabase() {
  const legacy = window.AC_LEGACY;
  return legacy && typeof legacy.getSupabase === "function"
    ? legacy.getSupabase()
    : null;
}

function callLoadAndRenderWhoami() {
  const legacy = window.AC_LEGACY;
  if (legacy && typeof legacy.loadAndRenderWhoami === "function") {
    return legacy.loadAndRenderWhoami();
  }
  return Promise.resolve();
}

export async function refreshAccountAvatarPreview(user) {
  const img = $("profile-avatar-preview");
  if (!img) return;
  const email = (user && user.email) || "";
  // Prefer the uploaded avatar (cached on whoami after the
  // sidebar's first /v1/user/profile fetch). Falls through to
  // Gravatar, then initials, matching the sidebar precedence.
  const w = getWhoami();
  if (w && w.avatar_data_url) {
    img.src = w.avatar_data_url;
  } else if (email) {
    img.src = await gravatarURL(email);
  } else {
    img.src = inlineInitialAvatar(
      (user && user.email) || (user && user.id) || "",
    );
  }
}

async function uploadAvatarDataURL(dataURL) {
  const supabase = getSupabase();
  if (!supabase) throw new Error("not signed in");
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error("not signed in");
  const r = await fetch(COORDINATOR + "/v1/user/avatar", {
    method: "PUT",
    headers: {
      Authorization: "Bearer " + session.access_token,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ data_url: dataURL }),
  });
  if (!r.ok) {
    let body = {};
    try {
      body = await r.json();
    } catch {}
    throw new Error(body.error || "HTTP " + r.status);
  }
}

async function deleteAvatar() {
  const supabase = getSupabase();
  if (!supabase) return;
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) return;
  await fetch(COORDINATOR + "/v1/user/avatar", {
    method: "DELETE",
    headers: { Authorization: "Bearer " + session.access_token },
  });
}

// ---- module init: wire avatar trigger + file input + remove button ----
//
// Done at module init (not inside renderAccountPage) so the
// listeners survive page navigations and don't double-fire.
function wireAvatarUpload() {
  const trigger = $("profile-avatar-trigger");
  const input = $("profile-avatar-input");
  const clearBtn = $("profile-avatar-clear");
  if (!trigger || !input) return;
  trigger.addEventListener("click", () => input.click());
  input.addEventListener("change", async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const dataURL = await resizeImageToDataURL(file, 128);
      await uploadAvatarDataURL(dataURL);
      patchWhoami({ avatar_data_url: dataURL });
      // Repaint the Account preview + sidebar badge so the new
      // avatar shows immediately without a /v1/user/profile round-trip.
      const preview = $("profile-avatar-preview");
      const sidebar = document.getElementById("whoami-avatar");
      if (preview) preview.src = dataURL;
      if (sidebar) sidebar.src = dataURL;
      showToast("success", "Avatar updated.");
    } catch (err) {
      showToast("error", "Avatar upload failed: " + (err.message || err));
    } finally {
      // Clear the input so picking the same file twice re-fires change.
      e.target.value = "";
    }
  });
  if (clearBtn) {
    clearBtn.addEventListener("click", async () => {
      try {
        await deleteAvatar();
        patchWhoami({ avatar_data_url: null });
        // Refresh both previews — they'll fall back to Gravatar / initials.
        await callLoadAndRenderWhoami();
        const supabase = getSupabase();
        let user = null;
        try {
          if (supabase) {
            ({
              data: { user },
            } = await supabase.auth.getUser());
          }
        } catch {}
        await refreshAccountAvatarPreview(user);
        showToast("success", "Avatar removed.");
      } catch (err) {
        showToast("error", "Could not remove avatar: " + (err.message || err));
      }
    });
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", wireAvatarUpload);
} else {
  wireAvatarUpload();
}
